// src/components/RPA/buttons/ConductTestingButton.js
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const api = process.env.REACT_APP_API;

const POLL_MS = 8000;        // Poll every 8 seconds (testing takes longer)
const MAX_MINUTES = 20;      // Stop polling after 20 minutes

const ConductTestingButton = ({ docId, disabledHint, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  // analysis result and/or status
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  // progress & status
  const [phase, setPhase] = useState('idle');      // idle | queued | testing | done | error
  const [queuedMsg, setQueuedMsg] = useState('');
  const [elapsedSec, setElapsedSec] = useState(0);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  // Testing parameters
  const [confidenceLevel, setConfidenceLevel] = useState(95);
  const [tolerableError, setTolerableError] = useState(5);

  const timerRef = useRef(null);
  const tickRef = useRef(null);

  // --- helpers --------------------------------------------------------------

  const stopTimers = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  };

  const closePanel = () => {
    stopTimers();
    setOpen(false);
  };

  // Poll /admin/rpa/docs for this docId, return doc or null
  const fetchDocMeta = async () => {
    const res = await axios.get(`${api}/admin/rpa/docs`, { withCredentials: true });
    const docs = Array.isArray(res?.data?.items) ? res.data.items : [];
    return docs.find(d => String(d._id) === String(docId)) || null;
  };

  // Polling loop when queued
  const beginPolling = () => {
    // elapsed timer
    tickRef.current = setInterval(() => {
      setElapsedSec(sec => sec + 1);
    }, 1000);

    // status poller
    timerRef.current = setInterval(async () => {
      try {
        const d = await fetchDocMeta();
        if (!d) return;

        // If testingResults present, we are done
        if (d.testingResults) {
          stopTimers();
          setPhase('done');
          setResult({
            success: true,
            testing: { 
              azure: true, 
              results: d.testingResults 
            },
          });
          if (typeof onDone === 'function') onDone(); // refresh doc list
          return;
        }

        // Still queued/working — update the UI hints
        if (d.testingQueuedAt) {
          setPhase('testing');
          setLastUpdatedAt(d.updatedAt || d.createdAt || null);
        }

        // Safety stop after MAX_MINUTES
        if (elapsedSec >= MAX_MINUTES * 60) {
          stopTimers();
          setPhase('error');
          setErr('Testing execution timed out. This may indicate complex testing requirements or large population size.');
        }
      } catch (e) {
        // transient errors — keep polling unless it persists too long
        console.warn('[RPA] testing status poll failed:', e?.message || e);
      }
    }, POLL_MS);
  };

  const run = async () => {
    if (!docId) return;
    setBusy(true);
    setErr('');
    setResult(null);
    setElapsedSec(0);
    setLastUpdatedAt(null);
    stopTimers();

    try {
      // kick off testing execution
      const res = await axios.post(
        `${api}/admin/rpa/docs/${docId}/conduct/testing`,
        { confidenceLevel, tolerableError },
        { withCredentials: true, validateStatus: () => true }
      );

      // 200 => testing is already available (cached) or completed inline
      if (res.status === 200) {
        setResult(res.data || { message: 'No data returned' });
        setPhase('done');
        setOpen(true);
        if (typeof onDone === 'function') onDone(); // refresh doc list
        return;
      }

      // 202 => queued; start polling
      if (res.status === 202) {
        setPhase('queued');
        setQueuedMsg(res.data?.message || 'Queued for testing execution...');
        setOpen(true);
        beginPolling();
        return;
      }

      // other => error
      console.error('[RPA] conduct testing unexpected:', res.status, res.data);
      setErr(res.data?.message || 'Testing execution failed.');
      setPhase('error');
      setOpen(true);
    } catch (e) {
      console.error('[RPA] conduct testing failed:', e);
      setErr('Testing execution failed.');
      setPhase('error');
      setOpen(true);
    } finally {
      setBusy(false);
    }
  };

  // Clean up timers if component unmounts or panel closes
  useEffect(() => {
    return () => stopTimers();
  }, []);

  // --- render helpers -------------------------------------------------------

  const renderSourceLabel = () => {
    if (!result) return null;

    // If Azure AI responded
    if (result.testing?.azure) {
      return (
        <p style={{ color: 'green', fontWeight: 'bold', marginBottom: 6 }}>
          ✅ AI-Powered Testing Execution Complete
        </p>
      );
    }

    // Otherwise fallback heuristic
    return (
      <p style={{ color: '#666', fontStyle: 'italic', marginBottom: 6 }}>
        ⚠️ Heuristic Testing (Fallback)
      </p>
    );
  };

  const renderTestingResults = () => {
    if (!result?.testing?.results) return null;

    let results;
    try {
      results = typeof result.testing.results === 'string' 
        ? JSON.parse(result.testing.results) 
        : result.testing.results;
    } catch {
      return <p style={{ color: '#666' }}>Unable to parse testing results</p>;
    }

    const execution = results.test_execution || {};
    const overallResults = results.overall_results || {};
    const attributeResults = results.attribute_results || [];
    const recommendations = results.recommendations || [];

    return (
      <div style={resultsContainer}>
        {/* Execution Summary */}
        <div style={executionBox}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#1f2937' }}>
            Testing Execution Summary
          </h4>
          <div style={{ display: 'grid', gap: 4, fontSize: 12 }}>
            <div><strong>Execution Date:</strong> {execution.execution_date ? new Date(execution.execution_date).toLocaleString() : 'Not specified'}</div>
            <div><strong>Sample Size:</strong> {execution.sample_size || 'Not specified'}</div>
            <div><strong>Population Coverage:</strong> {execution.population_coverage || 'Not specified'}</div>
            <div><strong>Testing Method:</strong> {execution.testing_method || 'Not specified'}</div>
          </div>
        </div>

        {/* Overall Results */}
        <div style={{ ...resultBox, border: getResultColor(overallResults.control_effectiveness) }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#1f2937' }}>
            Overall Audit Results
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
            <div><strong>Total Tests:</strong> {overallResults.total_tests || 0}</div>
            <div><strong>Total Exceptions:</strong> {overallResults.total_exceptions || 0}</div>
            <div><strong>Exception Rate:</strong> {overallResults.exception_rate || '0%'}</div>
            <div><strong>Control Effectiveness:</strong> {overallResults.control_effectiveness || 'Not determined'}</div>
            <div><strong>Material Weaknesses:</strong> {overallResults.material_weaknesses || 0}</div>
            <div><strong>Significant Deficiencies:</strong> {overallResults.significant_deficiencies || 0}</div>
          </div>
          
          {overallResults.audit_opinion_impact && overallResults.audit_opinion_impact !== 'none' && (
            <div style={{ marginTop: 8, padding: 8, background: '#fef2f2', borderRadius: 4, border: '1px solid #f87171' }}>
              <strong style={{ fontSize: 12, color: '#991b1b' }}>Audit Opinion Impact:</strong>
              <div style={{ fontSize: 11, color: '#991b1b', marginTop: 2 }}>
                {overallResults.audit_opinion_impact}
              </div>
            </div>
          )}
        </div>

        {/* Attribute Results */}
        {attributeResults.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#1f2937' }}>
              Testing Attribute Results ({attributeResults.length})
            </h4>
            <div style={attributesGrid}>
              {attributeResults.slice(0, 4).map((attr, idx) => (
                <div key={idx} style={attributeCard}>
                  <div style={{ fontWeight: 'bold', fontSize: 12, marginBottom: 4 }}>
                    {attr.attribute_id || `Attribute ${idx + 1}`}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 'bold', color: '#374151', marginBottom: 4 }}>
                    {attr.attribute_name}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, fontSize: 10, marginBottom: 6 }}>
                    <div><strong>Tests:</strong> {attr.tests_performed || 0}</div>
                    <div><strong>Passed:</strong> {attr.tests_passed || 0}</div>
                    <div><strong>Failed:</strong> {attr.tests_failed || 0}</div>
                    <div><strong>Rate:</strong> {attr.failure_rate || '0%'}</div>
                  </div>
                  <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 6 }}>
                    {attr.attribute_conclusion}
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <span style={{ 
                      ...badge, 
                      background: attr.tests_failed > 0 ? '#fef2f2' : '#f0fdf4',
                      color: attr.tests_failed > 0 ? '#991b1b' : '#065f46'
                    }}>
                      {attr.tests_failed > 0 ? 'FAILED' : 'PASSED'}
                    </span>
                    {attr.management_letter_point === 'yes' && (
                      <span style={{ ...badge, background: '#fef3c7', color: '#92400e' }}>
                        ML POINT
                      </span>
                    )}
                  </div>
                </div>
              ))}
              {attributeResults.length > 4 && (
                <div style={{ ...attributeCard, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>
                    +{attributeResults.length - 4} more attributes...
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Recommendations */}
        {recommendations.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#1f2937' }}>
              Audit Recommendations ({recommendations.length})
            </h4>
            <div style={recommendationsContainer}>
              {recommendations.slice(0, 3).map((rec, idx) => (
                <div key={idx} style={recommendationItem}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ 
                      ...badge, 
                      background: getPriorityColor(rec.priority),
                      color: getPriorityTextColor(rec.priority)
                    }}>
                      {rec.priority?.toUpperCase() || 'MEDIUM'}
                    </span>
                    {rec.management_response_required === 'yes' && (
                      <span style={{ fontSize: 10, color: '#dc2626' }}>Response Required</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: '#374151', marginBottom: 4 }}>
                    {rec.recommendation}
                  </div>
                  {rec.implementation_timeline && (
                    <div style={{ fontSize: 10, color: '#6b7280' }}>
                      Timeline: {rec.implementation_timeline}
                    </div>
                  )}
                </div>
              ))}
              {recommendations.length > 3 && (
                <div style={{ fontSize: 11, color: '#6b7280', padding: 8 }}>
                  +{recommendations.length - 3} more recommendations...
                </div>
              )}
            </div>
          </div>
        )}

        {/* Success Message */}
        {attributeResults.length === 0 && recommendations.length === 0 && (
          <div style={{ marginTop: 16, padding: 12, background: '#f0fdf4', borderRadius: 6, border: '1px solid #22c55e' }}>
            <strong style={{ fontSize: 12, color: '#15803d' }}>Testing Complete</strong>
            <div style={{ fontSize: 11, color: '#15803d', marginTop: 2 }}>
              No significant exceptions or recommendations identified. Controls appear to be operating effectively.
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderStatus = () => {
    if (phase === 'queued') {
      return (
        <div style={statusBox}>
          <strong>Queued for Testing</strong>
          <div style={{ fontSize: 13, color: '#555' }}>{queuedMsg}</div>
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Spinner />
            <span style={{ fontSize: 12, color: '#777' }}>
              Preparing testing execution... {formatElapsed(elapsedSec)}
            </span>
          </div>
        </div>
      );
    }

    if (phase === 'testing') {
      return (
        <div style={statusBox}>
          <strong>Conducting Testing...</strong>
          <div style={{ fontSize: 13, color: '#555', marginTop: 4 }}>
            Applying testing attributes to population sample and analyzing results
          </div>
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Spinner />
            <span style={{ fontSize: 12, color: '#777' }}>
              This can take several minutes for large populations. Elapsed {formatElapsed(elapsedSec)}
              {lastUpdatedAt ? ` • Last update ${new Date(lastUpdatedAt).toLocaleTimeString()}` : ''}
            </span>
          </div>
          <ProgressBar />
        </div>
      );
    }

    if (phase === 'error') {
      return <p style={{ color: 'crimson' }}>{err || 'Something went wrong.'}</p>;
    }

    return null;
  };

  const renderTestingParameters = () => {
    if (phase !== 'idle') return null;

    return (
      <div style={{ marginBottom: 12 }}>
        <div style={{ marginBottom: 8, fontSize: 13, color: '#555' }}>
          Configure testing parameters before execution:
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 500 }}>
              Confidence Level
            </label>
            <select
              value={confidenceLevel}
              onChange={(e) => setConfidenceLevel(parseInt(e.target.value))}
              style={{ width: '100%', padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12 }}
            >
              <option value={90}>90%</option>
              <option value={95}>95%</option>
              <option value={99}>99%</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 500 }}>
              Tolerable Error
            </label>
            <select
              value={tolerableError}
              onChange={(e) => setTolerableError(parseInt(e.target.value))}
              style={{ width: '100%', padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12 }}
            >
              <option value={1}>1%</option>
              <option value={2}>2%</option>
              <option value={5}>5%</option>
              <option value={10}>10%</option>
            </select>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <button
        onClick={() => setOpen(true)}
        disabled={busy || !!disabledHint}
        title={disabledHint || 'Execute testing using formulated attributes on population data'}
        style={{ 
          background: '#dc2626', 
          color: 'white', 
          border: 'none', 
          padding: '4px 8px', 
          borderRadius: 4,
          fontSize: 12,
          cursor: disabledHint ? 'not-allowed' : 'pointer',
          opacity: disabledHint ? 0.6 : 1
        }}
      >
        {busy ? 'Testing…' : 'Conduct Testing'}
      </button>

      {open && (
        <div style={panel}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <strong>Conduct Testing Execution</strong>
            <button onClick={closePanel} style={{ padding: '2px 8px' }}>Close</button>
          </div>

          {renderTestingParameters()}

          {phase === 'idle' && (
            <button
              onClick={run}
              disabled={busy}
              style={{ 
                background: '#dc2626', 
                color: 'white', 
                border: 'none', 
                padding: '6px 12px', 
                borderRadius: 4,
                fontSize: 12,
                cursor: 'pointer'
              }}
            >
              {busy ? 'Starting...' : 'Execute Testing'}
            </button>
          )}

          {renderStatus()}

          {/* Show the final result once ready */}
          {phase === 'done' && !err && result && (
            <>
              {renderSourceLabel()}
              {renderTestingResults()}
              
              {/* Raw JSON for debugging */}
              <details style={{ marginTop: 12 }}>
                <summary style={{ fontSize: 12, color: '#666', cursor: 'pointer' }}>
                  Raw Results (Debug)
                </summary>
                <pre style={{ whiteSpace: 'pre-wrap', margin: '8px 0 0 0', fontSize: 10, background: '#f8f9fa', padding: 8, borderRadius: 4, maxHeight: 200, overflow: 'auto' }}>
                  {typeof result.testing.results === 'string' 
                    ? result.testing.results 
                    : JSON.stringify(result.testing.results, null, 2)}
                </pre>
              </details>
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* ------------------------------- helpers & styles ---------------------------- */

function getResultColor(effectiveness) {
  switch (effectiveness) {
    case 'effective': return '1px solid #22c55e';
    case 'partially_effective': return '1px solid #f59e0b';
    case 'ineffective': return '1px solid #ef4444';
    default: return '1px solid #e5e7eb';
  }
}

function getPriorityColor(priority) {
  switch (priority) {
    case 'critical': return '#fef2f2';
    case 'high': return '#fef3c7';
    case 'medium': return '#eff6ff';
    case 'low': return '#f0fdf4';
    default: return '#f3f4f6';
  }
}

function getPriorityTextColor(priority) {
  switch (priority) {
    case 'critical': return '#991b1b';
    case 'high': return '#92400e';
    case 'medium': return '#1e40af';
    case 'low': return '#065f46';
    default: return '#6b7280';
  }
}

function formatElapsed(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const panel = {
  marginTop: 8,
  background: '#f8f9fb',
  border: '1px solid #e2e5ea',
  padding: 12,
  borderRadius: 8,
  maxWidth: 900,
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
};

const statusBox = {
  background: '#fff',
  border: '1px solid #e6e9ef',
  padding: 10,
  borderRadius: 8,
  marginBottom: 10
};

const resultsContainer = {
  background: '#fff',
  border: '1px solid #e6e9ef',
  padding: 12,
  borderRadius: 8,
  marginBottom: 8
};

const executionBox = {
  background: '#f8fafc',
  border: '1px solid #cbd5e1',
  padding: 10,
  borderRadius: 6,
  marginBottom: 12
};

const resultBox = {
  background: '#fff',
  padding: 10,
  borderRadius: 6,
  marginBottom: 12
};

const attributesGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: 8
};

const attributeCard = {
  background: '#f9fafb',
  border: '1px solid #e5e7eb',
  padding: 10,
  borderRadius: 6,
  minHeight: 120
};

const recommendationsContainer = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8
};

const recommendationItem = {
  background: '#fffbeb',
  border: '1px solid #fbbf24',
  padding: 10,
  borderRadius: 6
};

const badge = {
  fontSize: 9,
  padding: '2px 6px',
  borderRadius: 12,
  fontWeight: 'bold',
  textTransform: 'uppercase'
};

const Spinner = () => (
  <span
    style={{
      width: 14,
      height: 14,
      border: '2px solid #cbd3e1',
      borderTop: '2px solid #dc2626',
      borderRadius: '50%',
      display: 'inline-block',
      animation: 'spin 1s linear infinite'
    }}
  />
);

const ProgressBar = () => (
  <div style={{ marginTop: 8, height: 8, background: '#e9edf5', borderRadius: 6, overflow: 'hidden' }}>
    <div style={{
      width: '70%',
      height: '100%',
      background: '#dc2626',
      animation: 'bar 3s ease-in-out infinite'
    }} />
    <style>{`
      @keyframes spin { 0% { transform: rotate(0deg);} 100% { transform: rotate(360deg);} }
      @keyframes bar {
        0% { transform: translateX(-100%); }
        50% { transform: translateX(30%); }
        100% { transform: translateX(150%); }
      }
    `}</style>
  </div>
);

export default ConductTestingButton;